<script setup>
import { useI18n } from 'vue-i18n';
import { centsToWholeUnits, formatPercent } from '@/utils/poolaReportFormat.js';

defineProps({
  // { grossCents, cashCents, poolshareCents, avgDiscountFraction } — see
  // useSupplierDealReport's composeMoney.
  money: { type: Object, required: true },
});

const { t } = useI18n({ useScope: 'global' });
</script>

<template>
  <div class="report-money" data-report-section="money">
    <h2 class="report-section-title">{{ t('poola.report.money.title') }}</h2>
    <div class="report-money__grid">
      <div class="report-money__cell">
        <p class="report-money__key">{{ t('poola.report.money.total') }}</p>
        <p class="report-money__value">
          <span class="num">{{ centsToWholeUnits(money.grossCents) }}</span>
          <small>{{ t('poola.pool.currency') }}</small>
        </p>
      </div>
      <div class="report-money__cell">
        <p class="report-money__key">{{ t('poola.report.money.cash') }}</p>
        <p class="report-money__value">
          <span class="num">{{ centsToWholeUnits(money.cashCents) }}</span>
          <small>{{ t('poola.pool.currency') }}</small>
        </p>
      </div>
      <div class="report-money__cell">
        <p class="report-money__key">{{ t('poola.report.money.poolshareAccepted') }}</p>
        <p class="report-money__value">
          <span class="num">{{ centsToWholeUnits(money.poolshareCents) }}</span>
          <small>{{ t('poola.pool.currency') }}</small>
        </p>
      </div>
      <div class="report-money__cell">
        <p class="report-money__key">{{ t('poola.report.money.avgDiscountPct') }}</p>
        <p class="report-money__value">
          <span class="num">{{ formatPercent(money.avgDiscountFraction) }}</span>
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped>
.report-money {
  display: flex;
  flex-direction: column;
  gap: 11px;
  padding-inline: var(--poolit-spacing-md, 16px);
}

.report-section-title {
  margin: 2px 0 0;
  font-size: 15px;
  font-weight: var(--poolit-font-weight-bold, 700);
  color: var(--report-text);
}

.report-money__grid {
  background: var(--report-surface-2);
  border: 1px solid var(--report-hairline);
  border-radius: var(--poolit-radius-lg, 16px);
  padding: 6px 4px;
  display: grid;
  grid-template-columns: 1fr 1fr;
}

.report-money__cell {
  padding: 12px 14px;
}

.report-money__cell:nth-child(1),
.report-money__cell:nth-child(2) {
  border-block-end: 1px solid var(--report-hairline);
}

.report-money__cell:nth-child(odd) {
  border-inline-start: 1px solid var(--report-hairline);
}

.report-money__key {
  margin: 0;
  font-size: 11.5px;
  color: var(--report-text-soft);
}

.report-money__value {
  margin: 3px 0 0;
  font-size: 18px;
  font-weight: var(--poolit-font-weight-bold, 700);
  color: var(--report-text);
}

.report-money__value .num {
  font-variant-numeric: tabular-nums;
}

.report-money__value small {
  font-size: 12px;
  font-weight: var(--poolit-font-weight-medium, 500);
  color: var(--report-text-soft);
  margin-inline-start: 3px;
}
</style>
